import React, { useState, useEffect } from 'react';
import {
  Box, Button, Paper, TextField, Typography, CircularProgress, Alert,
  Snackbar, FormControl, InputLabel, Select, MenuItem, Switch, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, useMediaQuery, useTheme
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import RuleIcon from '@mui/icons-material/Rule';
import { getAllKeyValuesV2, setKeyValueV2 } from '../../api/admin';
import { getCopilotModels } from '../../api/models';
import { getLibraryRules, updateLibraryRuleEnabled, updateLibraryRule } from '../../api/conceptgate';
import logger from '../../utils/logger';
import './AdminView.css';

const LLM_INSTRUCTIONS_KEY = 'conceptgate:llm_global_instructions';
const LLM_AGENT_MODEL_KEY = 'conceptgate:llm_agent_model';
const SEARCH_PROMPT_KEY = 'conceptgate:search_prompt_template';

const DEFAULT_MODEL = 'gpt-5-nano';

const ConceptGateSettingsTab = ({ auth }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [instructions, setInstructions] = useState('');
  const [originalInstructions, setOriginalInstructions] = useState('');
  const [agentModel, setAgentModel] = useState(DEFAULT_MODEL);
  const [originalAgentModel, setOriginalAgentModel] = useState(DEFAULT_MODEL);
  const [availableModels, setAvailableModels] = useState([]);
  const [searchPrompt, setSearchPrompt] = useState('latest financial news for {instrument} trading pair analysis');
  const [originalSearchPrompt, setOriginalSearchPrompt] = useState('latest financial news for {instrument} trading pair analysis');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Library rules state
  const [libraryRules, setLibraryRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load key-value settings
      const kvResponse = await new Promise((resolve) => {
        getAllKeyValuesV2(auth, resolve);
      });

      // Load available models from the database
      const modelsResponse = await new Promise((resolve) => {
        getCopilotModels(auth, resolve);
      });

      // Set available models (only enabled ones)
      if (Array.isArray(modelsResponse)) {
        const enabledModels = modelsResponse.filter(m => m.modelEnabled);
        setAvailableModels(enabledModels);
      }

      if (Array.isArray(kvResponse)) {
        const instructionsEntry = kvResponse.find(kv => kv.key === LLM_INSTRUCTIONS_KEY);
        if (instructionsEntry) {
          setInstructions(instructionsEntry.value || '');
          setOriginalInstructions(instructionsEntry.value || '');
        }

        const modelEntry = kvResponse.find(kv => kv.key === LLM_AGENT_MODEL_KEY);
        if (modelEntry) {
          setAgentModel(modelEntry.value || DEFAULT_MODEL);
          setOriginalAgentModel(modelEntry.value || DEFAULT_MODEL);
        }

        const searchPromptEntry = kvResponse.find(kv => kv.key === SEARCH_PROMPT_KEY);
        if (searchPromptEntry) {
          setSearchPrompt(searchPromptEntry.value || 'latest financial news for {instrument} trading pair analysis');
          setOriginalSearchPrompt(searchPromptEntry.value || 'latest financial news for {instrument} trading pair analysis');
        }
      }
    } catch (error) {
      logger.error('Error loading ConceptGate settings:', error);
      setSnackbar({ open: true, message: 'Failed to load', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadLibraryRules = async () => {
    setRulesLoading(true);
    try {
      await getLibraryRules(auth, (rules) => {
        if (rules && !rules.error) {
          setLibraryRules(rules);
        }
        setRulesLoading(false);
      }, { includeDisabled: true });
    } catch (error) {
      logger.error('Error loading library rules:', error);
      setRulesLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadLibraryRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (instructions !== originalInstructions) {
        await new Promise((resolve) => {
          setKeyValueV2(auth, resolve, {
            key: LLM_INSTRUCTIONS_KEY,
            description: 'Global instructions for ConceptGate LLM rules',
            value: instructions
          });
        });
        setOriginalInstructions(instructions);
      }

      if (agentModel !== originalAgentModel) {
        await new Promise((resolve) => {
          setKeyValueV2(auth, resolve, {
            key: LLM_AGENT_MODEL_KEY,
            description: 'Default LLM model for ConceptGate AI rules',
            value: agentModel
          });
        });
        setOriginalAgentModel(agentModel);
      }

      if (searchPrompt !== originalSearchPrompt) {
        await new Promise((resolve) => {
          setKeyValueV2(auth, resolve, {
            key: SEARCH_PROMPT_KEY,
            description: 'Template for news search queries',
            value: searchPrompt
          });
        });
        setOriginalSearchPrompt(searchPrompt);
      }

      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
    } catch (error) {
      logger.error('Error saving ConceptGate settings:', error);
      setSnackbar({ open: true, message: 'Failed to save', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = instructions !== originalInstructions ||
    agentModel !== originalAgentModel ||
    searchPrompt !== originalSearchPrompt;

  const handleDiscard = () => {
    setInstructions(originalInstructions);
    setAgentModel(originalAgentModel);
    setSearchPrompt(originalSearchPrompt);
  };

  // Library rules handlers
  const handleToggleEnabled = async (rule) => {
    const newEnabled = !rule.enabled;
    // Optimistic update
    setLibraryRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: newEnabled } : r));

    await updateLibraryRuleEnabled(auth, (result) => {
      if (result?.error) {
        // Revert on error
        setLibraryRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !newEnabled } : r));
        setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
      }
    }, rule.id, newEnabled);
  };

  const handleEditClick = (rule) => {
    setEditRule(rule);
    setEditForm({
      name: rule.name || '',
      description: rule.description || '',
      category: rule.category || '',
      executionOrder: rule.executionOrder || 0,
      failFast: rule.failFast || false,
      defaultConfigJson: rule.defaultConfigJson || '{}',
      agentPrompt: rule.agentPrompt || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editRule) return;
    setEditSaving(true);

    const updateData = {
      name: editForm.name,
      description: editForm.description,
      category: editForm.category,
      executionOrder: parseInt(editForm.executionOrder) || 0,
      failFast: editForm.failFast,
    };

    // Add type-specific fields
    if (editRule.ruleType === 'HARDCODED') {
      updateData.defaultConfigJson = editForm.defaultConfigJson;
    } else {
      updateData.agentPrompt = editForm.agentPrompt;
    }

    await updateLibraryRule(auth, (result) => {
      if (result && !result.error) {
        setLibraryRules(prev => prev.map(r => r.id === editRule.id ? { ...r, ...result } : r));
        setSnackbar({ open: true, message: 'Rule updated', severity: 'success' });
        setEditDialogOpen(false);
      } else {
        setSnackbar({ open: true, message: 'Failed to update rule', severity: 'error' });
      }
      setEditSaving(false);
    }, editRule.id, updateData);
  };

  if (loading) {
    return (
      <Box className="admin-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="admin-tab-content">
      {/* Header */}
      <Box className="admin-section-header">
        <Typography variant="h6">ConceptGate Settings</Typography>
        <Button
          variant="outlined"
          onClick={() => { loadSettings(); loadLibraryRules(); }}
          startIcon={<RefreshIcon />}
          size="small"
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Library Rules Management Section */}
      <Paper className="admin-settings-card">
        <Box className="admin-settings-card-header">
          <RuleIcon className="admin-settings-icon" />
          <Typography fontWeight="600">Library Pipeline Templates</Typography>
        </Box>
        <Typography variant="body2" className="admin-settings-desc">
          Enable or disable validation rules. Disabled rules will not appear in user's library.
        </Typography>

        {rulesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Desktop table view */}
            <div className="admin-table-desktop">
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rule</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="center">Enabled</TableCell>
                      <TableCell align="center">Edit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {libraryRules.map((rule) => (
                      <TableRow key={rule.id} sx={{ opacity: rule.enabled ? 1 : 0.6 }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">{rule.name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {rule.description?.substring(0, 60)}{rule.description?.length > 60 ? '...' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={rule.ruleType === 'LLM_AGENT' ? 'AI' : 'Static'}
                            color={rule.ruleType === 'LLM_AGENT' ? 'secondary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {rule.category}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={rule.enabled}
                            onChange={() => handleToggleEnabled(rule)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleEditClick(rule)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            {/* Mobile card view */}
            <div className="admin-cards-mobile">
              {libraryRules.map((rule) => (
                <div key={rule.id} className="admin-mobile-card" style={{ opacity: rule.enabled ? 1 : 0.6 }}>
                  <div className="admin-mobile-card-header">
                    <Typography variant="body2" fontWeight="600">{rule.name}</Typography>
                    <Chip
                      size="small"
                      label={rule.ruleType === 'LLM_AGENT' ? 'AI' : 'Static'}
                      color={rule.ruleType === 'LLM_AGENT' ? 'secondary' : 'default'}
                      variant="outlined"
                    />
                  </div>
                  <div className="admin-mobile-card-body">
                    <Typography variant="caption" color="text.secondary">
                      {rule.description?.substring(0, 80)}{rule.description?.length > 80 ? '...' : ''}
                    </Typography>
                    <div className="admin-mobile-card-row">
                      <span className="admin-mobile-card-row-label">Category</span>
                      <span className="admin-mobile-card-row-value" style={{ textTransform: 'capitalize' }}>
                        {rule.category}
                      </span>
                    </div>
                    <div className="admin-mobile-card-row">
                      <span className="admin-mobile-card-row-label">Enabled</span>
                      <span className="admin-mobile-card-row-value">
                        <Switch
                          checked={rule.enabled}
                          onChange={() => handleToggleEnabled(rule)}
                          size="small"
                        />
                      </span>
                    </div>
                  </div>
                  <div className="admin-mobile-card-actions">
                    <Button
                      size="small"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={() => handleEditClick(rule)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Paper>

      {/* Agent Model Section */}
      <Paper className="admin-settings-card">
        <Box className="admin-settings-card-header">
          <SmartToyIcon className="admin-settings-icon" />
          <Typography fontWeight="600">LLM Agent Model</Typography>
        </Box>
        <Typography variant="body2" className="admin-settings-desc">
          Default AI model for LLM-based validation rules.
        </Typography>
        <FormControl fullWidth size="small" className="admin-settings-control">
          <InputLabel>Model</InputLabel>
          <Select
            value={agentModel}
            label="Model"
            onChange={(e) => setAgentModel(e.target.value)}
          >
            {availableModels.map((model) => (
              <MenuItem key={model.modelId} value={model.modelId}>
                {model.modelName} - {model.modelDescription || 'No description'}
              </MenuItem>
            ))}
            {availableModels.length === 0 && (
              <MenuItem value={DEFAULT_MODEL} disabled>
                Loading models...
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Paper>

      {/* Search Settings Section */}
      <Paper className="admin-settings-card">
        <Box className="admin-settings-card-header">
          <SearchIcon className="admin-settings-icon" />
          <Typography fontWeight="600">Web Search Configuration</Typography>
        </Box>
        <Typography variant="body2" className="admin-settings-desc">
          Query template for news search. Use <code>{"{instrument}"}</code> as placeholder.
        </Typography>
        <TextField
          label="Search Prompt Template"
          value={searchPrompt}
          onChange={(e) => setSearchPrompt(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          placeholder="news for {instrument}"
        />
      </Paper>

      {/* Instructions Section */}
      <Paper className="admin-settings-card">
        <Box className="admin-settings-card-header">
          <PsychologyIcon className="admin-settings-icon" />
          <Typography fontWeight="600">Global LLM Instructions</Typography>
        </Box>
        <Typography variant="body2" className="admin-settings-desc">
          Instructions applied to ALL LLM-based validation rules. Set trading philosophy, risk preferences, or criteria.
        </Typography>
        <Alert severity="info" className="admin-settings-alert">
          Changes apply to future validations only.
        </Alert>
        <TextField
          label="LLM Instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          fullWidth
          multiline
          rows={10}
          size="small"
          placeholder={`Example:
- Prioritize capital preservation
- Require 2:1 risk-reward ratio
- Flag trades >2% account risk`}
          className="admin-monospace-input"
        />

        {hasChanges && (
          <Box className="admin-save-bar">
            <Button variant="outlined" onClick={handleDiscard} size="small">
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              size="small"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Edit Rule Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Rule: {editRule?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={editForm.name || ''}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              value={editForm.description || ''}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={editForm.category || ''}
                label="Category"
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
              >
                <MenuItem value="risk">Risk</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
                <MenuItem value="timing">Timing</MenuItem>
                <MenuItem value="psychology">Psychology</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Execution Order"
              type="number"
              value={editForm.executionOrder || 0}
              onChange={(e) => setEditForm({ ...editForm, executionOrder: e.target.value })}
              fullWidth
              size="small"
            />
            {editRule?.ruleType === 'HARDCODED' && (
              <TextField
                label="Default Config (JSON)"
                value={editForm.defaultConfigJson || '{}'}
                onChange={(e) => setEditForm({ ...editForm, defaultConfigJson: e.target.value })}
                fullWidth
                multiline
                rows={4}
                size="small"
              />
            )}
            {editRule?.ruleType === 'LLM_AGENT' && (
              <TextField
                label="Agent Prompt"
                value={editForm.agentPrompt || ''}
                onChange={(e) => setEditForm({ ...editForm, agentPrompt: e.target.value })}
                fullWidth
                multiline
                rows={8}
                size="small"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={editSaving}
            startIcon={editSaving && <CircularProgress size={16} color="inherit" />}
          >
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConceptGateSettingsTab;
